import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Skeleton } from "@material-ui/core";
import { RootState } from "../../store";
import { initializeMaps } from "./googleMapSlice";
import Map, { MapProps } from "./components/Map";

interface GoogleMapProps extends MapProps {
    loading?: boolean;
    className?: string;
}

// This is a wrapper component around the actual Map component that takes care of
// waiting for the google maps api to load
export default ({
    options,
    center,
    markers = [],
    markersClusterDetails,
    movingMarkers = [],
    movingMarkerClusterDetails,
    staticRouteInfo,
    loading = false,
    className,
    polyLines,
}: GoogleMapProps) => {
    const mapsInitialized = useSelector<RootState>(
        (state) => state.googleMap.initialized,
    );
    const dispatch = useDispatch();

    // We check if google maps has been loaded or not. If so we don't do anything special.
    // If not, we listen for the MAPS_LOADED event to fire (this will fire when maps has loaded)
    // Which will dispatch the initializeMap action and update this component to display the map
    useEffect(() => {
        const mapsLoadedCallback = () => {
            dispatch(initializeMaps());
        };

        // In the event that the map has loaded and fired the event but not set the global object
        // we double check in the map component onMount and fire the event
        if (window.google?.maps) {
            dispatch(initializeMaps());
        }

        // Only add the event listener on mount if the google maps api is not loaded yet
        if (!mapsInitialized) {
            document.body.addEventListener("MAPS_LOADED", mapsLoadedCallback);
        }

        return () => {
            // removes the event listener if the Maps component gets destroyed
            document.body.removeEventListener("MAPS_LOADED", mapsLoadedCallback);
        };
    }, []);

    // If the map and data is loaded then we show the map, otherwise we show the
    // skeleton loading
    return mapsInitialized && !loading && options ? (
        <div className={className}>
            <Map
                options={options}
                markers={markers}
                markersClusterDetails={markersClusterDetails}
                movingMarkers={movingMarkers}
                movingMarkerClusterDetails={movingMarkerClusterDetails}
                staticRouteInfo={staticRouteInfo}
                center={center}
                polyLines={polyLines}
            />
        </div>
    ) : <Skeleton className={className} />;
};
